import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import fetch from 'unfetch';
import { Sub1Button, PrimaryButton } from '@components/atoms';
import { HorizontalTable, UserPermission } from '@components/organisms';
import { dialogContentsState, UserDetailField } from '@components/templates';
import { useSetRecoilState } from 'recoil';
import { ADMIN_ACCESS_TOKEN_KEY, getFetcher } from '../../../../App';
import { ErrorCode, ErrorMessage } from '@variables/error';
import {
  DetailTableStyle,
  DetailButtonsSection,
  RightButtonSection,
  DeleteAccountButtonCss,
  BackButtonStyle,
  SubmitButtonStyle,
} from './style';
import { checkEmail, checkId, checkName, checkPassword } from '@pages/Popup/CreateAccount';
import _, { debounce } from 'lodash';
import { submitDebounce } from '../../../../utils/lib';

interface UserDetailInfo {
  activated: boolean;
  createdDate: string;
  deleted: boolean;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  permissionCombination: string;
  position: string | null;
  toDate: string;
  userId: string;
}

const UserPermissionDetail = () => {
  const random = React.useRef(Date.now());
  const navigate = useNavigate();
  const { userNumber, currentPage } = useParams();
  const [accessToken, setAccessToken] = useState('');
  const [detailFields, setDetailFields] = useState<UserDetailInfo>();
  const [defaultDetailFields, setDefaultDetailFields] = useState<UserDetailInfo>();
  const [disabledSubmit, setDisabledSubmit] = useState(true);
  const [validAll, setValidAll] = useState({
    fstName: false,
    lastName: false,
    email: false,
    pwd: false,
  });

  const setDialogContents = useSetRecoilState(dialogContentsState);

  const url = `api/user/${userNumber}`;

  const { data, error, mutate } = useSWR(!!accessToken.length ? [{ url, accessToken }, random] : null, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const checkSubmit = useCallback(() => {
    if (
      !_.isEqual(detailFields, defaultDetailFields) &&
      !validAll.email &&
      !validAll.pwd &&
      !validAll.fstName &&
      !validAll.lastName
    ) {
      setDisabledSubmit(false);
    } else {
      setDisabledSubmit(true);
    }
  }, [validAll, defaultDetailFields, defaultDetailFields]);

  /**
   * blur시 submit 변경 처리
   */
  useEffect(() => {
    checkSubmit();
  }, [validAll]);

  useEffect(() => {
    if (data) {
      setDetailFields(data.result);
      setDefaultDetailFields(data.result);
    }
  }, [data]);

  useEffect(() => {
    if (
      (detailFields?.toDate !== defaultDetailFields?.toDate ||
        detailFields?.permissionCombination !== defaultDetailFields?.permissionCombination ||
        detailFields?.activated !== defaultDetailFields?.activated) &&
      !validAll.email &&
      !validAll.pwd &&
      !validAll.fstName &&
      !validAll.lastName
    ) {
      setDisabledSubmit(false);
    } else {
      setDisabledSubmit(true);
    }
  }, [detailFields]);

  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  if (error) {
    return <b>error</b>;
  }

  if (!data) {
    return <b>Loading...</b>;
  }
  if (!data.result) {
    if (data.code === 'UNAUTHORIZED') {
      return <Navigate to={'/'} replace={true} />;
    }
    return (
      <div>
        <>Error : {data.code || 'unknown'} </>
      </div>
    );
  }

  const updateAccount = async () => {
    await getFetcher({
      method: 'PATCH',
      accessToken,
      body: detailFields,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'success',
            body: 'Account has been updated successfully.',
            footer: (
              <PrimaryButton
                onClick={() => {
                  setDialogContents({ type: 'hidden' });
                  navigate('/permissions', { replace: true });
                }}
              >
                Confirm
              </PrimaryButton>
            ),
          });
        } else {
          let header = '';
          let body = '';
          if (ErrorCode[r.code]) {
            header = `${ErrorCode[r.code]}`;
            body = `${ErrorMessage[ErrorCode[r.code]]}`;
          } else {
            header = r.code;
          }

          setDialogContents({
            type: 'error',
            header: `${header}`,
            body: <>{body}</>,
            footer: (
              <PrimaryButton
                onClick={() => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                ok
              </PrimaryButton>
            ),
          });
        }
      })
      .catch((e) => {
        openDialog('Unexpected Error on Update Account', 'Retry', updateAccount);
      });
  };

  const deleteAccount = async () => {
    await getFetcher({
      method: 'DELETE',
      accessToken,
      body: detailFields,
    })(url)
      .then((r) => {
        setDialogContents({ type: 'hidden' });
        if (r.code === '200') {
          setDialogContents({
            type: 'success',
            body: 'Account has been updated successfully.',
            footer: (
              <PrimaryButton
                onClick={() => {
                  setDialogContents({ type: 'hidden' });
                  navigate('/permissions', { replace: true });
                }}
              >
                Confirm
              </PrimaryButton>
            ),
          });
        } else {
          let header = '';
          let body = '';
          if (ErrorCode[r.code]) {
            header = `${ErrorCode[r.code]}`;
            body = `${ErrorMessage[ErrorCode[r.code]]}`;
          } else {
            header = r.code;
          }

          setDialogContents({
            type: 'error',
            header: `${header}`,
            body: <>{body}</>,
            footer: (
              <PrimaryButton
                onClick={() => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                ok
              </PrimaryButton>
            ),
          });
        }
      })
      .catch((e) => {
        openDialog('Unexpected Error on Delete Account', 'Retry', deleteAccount);
      });
  };

  const onChangeDetailFeilds = (e: any) => {
    const _detailFields = JSON.parse(JSON.stringify(detailFields));
    if (!e.target.value && e.target.name === 'password') {
      delete _detailFields.password;
    }

    setDetailFields((prev: any) => ({
      ...data.result,
      ..._detailFields,
      [`${e.target.name}`]: e.target.value,
    }));
  };

  const openDialog = (body: string, buttonLabel: string, buttonEvent: any) => {
    setDialogContents({
      type: 'plain',
      body,
      footer: (
        <>
          <Sub1Button onClick={() => setDialogContents({ type: 'hidden' })}>Cancel</Sub1Button>
          <PrimaryButton onClick={buttonEvent}>{buttonLabel}</PrimaryButton>
        </>
      ),
    });
  };

  return (
    <>
      <HorizontalTable styles={DetailTableStyle} className={'active'}>
        {detailFields && (
          <UserDetailField {...detailFields} onChange={onChangeDetailFeilds} valid={validAll} setValid={setValidAll} />
        )}
      </HorizontalTable>
      <DetailButtonsSection>
        <Sub1Button
          styles={DeleteAccountButtonCss}
          onClick={() => {
            openDialog('Are you sure you want to delete this account?', 'Delete', submitDebounce(deleteAccount));
          }}
        >
          Delete account
        </Sub1Button>
        <RightButtonSection>
          <Sub1Button onClick={() => navigate('/permissions')} styles={BackButtonStyle}>
            Back
          </Sub1Button>
          <PrimaryButton
            styles={SubmitButtonStyle}
            disabled={disabledSubmit}
            onClick={() => {
              openDialog('Do you want to save the Changes?', 'Save', submitDebounce(updateAccount));
            }}
          >
            Submit
          </PrimaryButton>
        </RightButtonSection>
      </DetailButtonsSection>
    </>
  );
};

export default UserPermissionDetail;
